<script>
import {computed, defineComponent, onBeforeMount, reactive, ref, watch} from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Editor from "primevue/editor";
import MainCard from "@/components/cards/MainCard";
import Breadcrumb from "@/components/Breadcrumb";
import ButtonSuccess from "@/components/buttons/ButtonSuccess";
import ButtonFileUpload from "@/components/buttons/ButtonFileUpload.vue";
import ImageCard from "@/components/cards/ImageCard.vue";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import {useVuelidate} from "@vuelidate/core";
import {useApiCall} from "@/composables/useApiCall";
import {
  addAdvantageInfrastructureApiCall, addInfrastructureItemApiCall,
  deleteAdvantageInfrastructureApiCall,
  deletePhotoInfrastructureApiCall,
  infrastructureItemApiCall,
  updateAdvantageInfrastructureApiCall,
  updateInfrastructureItemApiCall,
  uploadPhotoInfrastructureApiCall,
  deleteInfrastructureApiCall,
  updatePhotoCaptionApiCall,
  updatePriceInfrastructureApiCall,
  deletePriceInfrastructureApiCall,
} from "@/services/infrastructure";
import {required} from "@/utils/i18n-validators";
import Skeleton from "primevue/skeleton"
import BrandCard from "@/components/cards/BrandCard.vue";
import WebsiteAddButton from "@/components/website/WebsiteAddButton.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import InfrastructureImages from "@/components/website/InfrastructureImages.vue";
import Checkbox from "primevue/checkbox";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Checkbox,
    InfrastructureImages,
    ConfirmationModal,
    WebsiteAddButton,
    BrandCard,
    ButtonSuccess,
    Button,
    InputText,
    MainCard,
    Breadcrumb,
    Editor,
    ButtonFileUpload,
    ImageCard,
    Skeleton,
  },
  setup() {
    const {t} = useI18n();
    const store = useStore();
    const router = useRouter()
    const route = useRoute()
    const errors = ref(null)
    const breadcrumbs = ref([]);
    const isLoading = ref(true)
    const form = reactive({
      fields: {
        id: 'newItem',
        title_ru: '',
        description_ru: '',
        title_en: '',
        description_en: '',
        photos: [],
        advantages: [],
        link: '',
        request_id: false,
        assigned_by: '',
        price: null,
      }
    })
    const isPermanent = computed(() => route.name === 'website')
    const request_id = ref(false)
    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => !route.params.id || (isPermanent.value && !form.fields.title_ru) ? 'title_ru' : `title_${lang.value}`);
    const descriptionField = computed(() => !route.params.id || (isPermanent.value && !form.fields.title_ru) ? 'description_ru' : `description_${lang.value}`);
    const rules = computed(() => {
      return {
        id: {},
        [titleField.value]: {required},
        [descriptionField.value]: {required},
        photos: {required},
        advantages: {},
        assigned_by: request_id.value ? {required} : {},
      }
    });
    const v$ = useVuelidate(rules, form.fields, { $scope: 'advantages' });
    const deleteUuids = ref([])
    const deleteItem = ref(null)
    const validError = ref(false)
    const visible = ref(false)
    const isError = computed(() =>
        contentUpdateError.value ||
        contentAddError.value ||
        contentAddPhotoError.value ||
        contentDeletePhotoError.value ||
        contentAddAdvantageError.value ||
        contentUpdateAdvantageError.value ||
        captionUpdateError.value
    )
    const isUpdateLoading = computed(() =>
        isContentUpdateLoading.value ||
        isContentAddLoading.value ||
        isContentAddPhotoLoading.value ||
        isContentDeletePhotoLoading.value ||
        isContentAddAdvantageLoading.value ||
        isContentUpdateAdvantageLoading.value ||
        isCaptionUpdateLoading.value
    )
    const {
      data: contentData,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(infrastructureItemApiCall, true)

    const {
      isLoading: isContentUpdateLoading,
      data: contentUpdateData,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateInfrastructureItemApiCall, true)

    const {
      isLoading: isCaptionUpdateLoading,
      data: captionUpdateData,
      error: captionUpdateError,
      executeApiCall: updateCaptionAction,
    } = useApiCall(updatePhotoCaptionApiCall, true)


    const {
      isLoading: isContentDeleteLoading,
      data: contentDeleteData,
      error: contentDeleteError,
      executeApiCall: deleteContentAction,
    } = useApiCall(    deleteInfrastructureApiCall, true)

    const {
      isLoading: isContentAddLoading,
      data: contentAddData,
      error: contentAddError,
      executeApiCall: addContentAction,
    } = useApiCall(addInfrastructureItemApiCall, true)

    const {
      isLoading: isContentAddPhotoLoading,
      data: contentAddPhotoData,
      error: contentAddPhotoError,
      executeApiCall: addContentPhotoAction,
    } = useApiCall(uploadPhotoInfrastructureApiCall, true)

    const {
      isLoading: isContentDeletePhotoLoading,
      data: contentDeletePhotoData,
      error: contentDeletePhotoError,
      executeApiCall: deleteContentPhotoAction,
    } = useApiCall(deletePhotoInfrastructureApiCall, true)

    const {
      isLoading: isUpdatePriceLoading,
      data: updatePriceData,
      error: updatePriceError,
      executeApiCall: updatePriceAction,
    } = useApiCall(updatePriceInfrastructureApiCall, true)

    const {
      isLoading: isDeletePriceLoading,
      data: deletePriceData,
      error: deletePriceError,
      executeApiCall: deletePriceAction,
    } = useApiCall(deletePriceInfrastructureApiCall, true)

    const {
      isLoading: isContentAddAdvantageLoading,
      data: contentAddAdvantageData,
      error: contentAddAdvantageError,
      executeApiCall: addAdvantageAction,
    } = useApiCall(addAdvantageInfrastructureApiCall, true)

    const {
      isLoading: isContentUpdateAdvantageLoading,
      data: contentUpdateAdvantageData,
      error: contentUpdateAdvantageError,
      executeApiCall: updateAdvantageAction,
    } = useApiCall(updateAdvantageInfrastructureApiCall, true)

    const {
      isLoading: isContentDeleteAdvantageLoading,
      data: contentDeleteAdvantageData,
      error: contentDeleteAdvantageError,
      executeApiCall: deleteAdvantageAction,
    } = useApiCall(deleteAdvantageInfrastructureApiCall, true)

    const loadFileMemory = (data) => {
      data.files.forEach((item) => {
        form.fields.photos.push({
          id: 'NewImage' + Date.now(),
          file: item,
          caption: '',
        })
      })
    }

    const changeAdvantages = (data, index) => {
      form.fields.advantages[index] = data
    }
    const deleteSlide  = (data) => {
      if (!data.id.toString().includes("NewImage")) {
        deleteUuids.value.push(data.uuid)
      }
      form.fields.photos = form.fields.photos.filter(item => item.id !== data.id)
    }

    const updateCaption = async (item, caption) => {
      const findIndex = form.fields.photos.findIndex(photo => photo.id === item.id)
      if (findIndex !== -1) {
        form.fields.photos[findIndex].caption = caption
      }
    }

    const update = async () => {
      errors.value = null
      const result = await v$.value.$validate()
      if(!result) return
      if (form.fields.id.toString().includes('newItem')) {
        try {
          const formData = new FormData()
          formData.append('title_ru', form.fields.title_ru)
          formData.append('description_ru', form.fields.description_ru)
          formData.append('title_en', form.fields.title_ru)
          formData.append('description_en', form.fields.description_ru)
          formData.append('photo', form.fields.photos[0].file)
          if (form.fields.photos[0].caption) {
            formData.append('caption', form.fields.photos[0].caption)
          }
          if (form.fields.link && !request_id.value) {
            formData.append('link', form.fields.link)
          }
          if (request_id.value) {
            formData.append('request_id', Number(request_id.value))
            formData.append('assigned_by', form.fields.assigned_by)
          }
          await addContentAction(formData)
          if (contentAddData.value) {
            form.fields.id = contentAddData.value.data.id
            if (form.fields.photos[0].caption) {
              await updateCaptionAction({
                id: form.fields.id,
                uuid: contentAddData.value.data.photos[0].uuid,
                fields: {
                  caption: form.fields.photos[0].caption,
                }
              })
            }
            form.fields.photos[0] = {...contentAddData.value.data.photos[0], caption: form.fields.photos[0].caption}
            contentData.value = { data: {...contentAddData.value.data, photos: form.fields.photos} }
          }
        } catch (e) {
          errors.value = contentAddError.value.data
          console.error(e)
          return
        }
      } else {
        try {
          await updateContentAction({
            id: isPermanent.value ? 'permanent' : form.fields.id,
            fields: {
              title_ru: form.fields.title_ru,
              description_ru: form.fields.description_ru,
              title_en: form.fields.title_en,
              description_en: form.fields.description_en,
              link: form.fields.link,
              request_id: Number(request_id.value),
              assigned_by: form.fields.assigned_by && request_id.value ? form.fields.assigned_by : null
            }
          })
        } catch (e) {
          errors.value = contentUpdateError.value.data
          console.error(e)
          return
        }
      }
      if (deleteUuids.value.length) {
        for (let i = 0; i < deleteUuids.value.length; i += 1) {
          try {
            await deleteContentPhotoAction({
              id: form.fields.id,
              fields: {
                uuid: deleteUuids.value[i]
              }
            })
            contentData.value.data.photos = contentData.value.data.photos.filter(item => item.uuid !== deleteUuids.value[i])
          } catch (e) {
            console.error(e)
            return
          }
        }
        deleteUuids.value = []
      }
      for (let i = 0; i < form.fields.photos.length; i += 1) {
        if (form.fields.photos[i].file) {
          try {
            const formData = new FormData()
            formData.append('photo', form.fields.photos[i].file)
            await addContentPhotoAction({
                  id: form.fields.id,
                  fields: formData,
                }
            )
            if (contentAddPhotoData.value?.data && form.fields.photos[i].caption) {
              await updateCaptionAction({
                id: form.fields.id,
                uuid: contentAddPhotoData.value?.data?.photos[i].uuid,
                fields: {
                  caption: form.fields.photos[i].caption,
                }
              })
              contentData.value.data.photos[i] = {...contentAddPhotoData.value.data?.photos[i], caption: form.fields.photos[i].caption}
              form.fields.photos[i] = {...contentAddPhotoData.value.data?.photos[i], caption: form.fields.photos[i].caption}
            }
          } catch (e) {
            errors.value = {imageError: contentAddPhotoError.value?.data, index: form.fields.photos[i].objectURL}
            console.error(e)
            return
          }
        } else {
          if (contentData.value.data.photos[i].caption !== form.fields.photos[i].caption) {
            if (form.fields.photos[i].caption) {
              await updateCaptionAction({
                id: form.fields.id,
                uuid: contentData.value.data.photos[i].uuid,
                fields: {
                  caption: form.fields.photos[i].caption,
                }
              })
              contentData.value.data.photos[i].caption = form.fields.photos[i].caption
            }
          }
        }
      }
      for (let i = 0; i < form.fields.advantages.length; i += 1) {
        if (!form.fields.advantages[i].id.toString().includes('newItem')) {
          const oldItem = contentData.value.data.advantages.findIndex(item => item.id === form.fields.advantages[i].id)
          if (contentData.value.data.advantages[oldItem] && (form.fields.advantages[i].photo.objectURL || contentData.value.data.advantages[oldItem].photo.id !== form.fields.advantages[i].photo.id || contentData.value.data.advantages[oldItem].text_ru !== form.fields.advantages[i].text_ru || contentData.value.data.advantages[oldItem].text_en !== form.fields.advantages[i].text_en)) {
            const formData = new FormData()
            if (form.fields.advantages[i].photo.objectURL) {
              formData.append('photo', form.fields.advantages[i].photo)
            }
            formData.append('text_ru', form.fields.advantages[i].text_ru)
            formData.append('text_en', form.fields.advantages[i].text_en)
            try {
              await updateAdvantageAction({
                id: form.fields.id,
                advantageId: form.fields.advantages[i].id,
                fields: formData
              })
              if (contentUpdateAdvantageData.value) {
                contentData.value.data.advantages[oldItem] = {...contentUpdateAdvantageData.value.data}
                form.fields.advantages[i] = {...contentUpdateAdvantageData.value.data}
              }
            } catch (e) {
              errors.value = {item: form.fields.advantages[i], error: contentUpdateAdvantageError.value.data}
              console.error(e)
              return
            }
          }
        } else {
          const formData = new FormData()
          formData.append('text_ru', form.fields.advantages[i].text_ru)
          formData.append('text_en', form.fields.advantages[i].text_ru)
          formData.append('photo', form.fields.advantages[i].photo)
          try {
            await addAdvantageAction({id: form.fields.id, fields: formData})
            if (contentAddAdvantageData.value?.data) {
              contentData.value.data.advantages.push(contentAddAdvantageData.value.data)
              form.fields.advantages[i] = {...contentAddAdvantageData.value.data}
            }
          } catch (e) {
            if (contentAddAdvantageError.value) {
              errors.value = {item: form.fields.advantages[i], error: contentAddAdvantageError.value.data}
            }
            console.error(e)
            return
          }
        }
      }
      if (form.fields.price?.size) {
        try {
          const formData = new FormData()
          formData.append('price', form.fields.price)
          await updatePriceAction({
            id: isPermanent.value ? 'permanent' : form.fields.id,
            fields: formData,
          })
        } catch (e) {
          errors.value = updatePriceError.value.data
          console.error(e)
          return
        }
      }
      if (!form.fields.price && contentData.value?.data?.price) {
        try {
          await deletePriceAction({
            id: isPermanent.value ? 'permanent' : form.fields.id,
          })
        } catch (e) {
          errors.value = deletePriceError.value.data
          console.error(e)
          return
        }
      }
      if (!isError.value && !isPermanent.value) {
        await router.push({name: 'website', query: {tab: 'infrastructure'}})
      }
    }

    const changeVisibleModal = (index = null) => {
      visible.value = !visible.value
      if (index || index === 0) {
        deleteItem.value = index
      }
    };

    const addAdvantages = () => {
      form.fields.advantages.push({
        id: `newItem${form.fields.length + 1}`,
        text_ru: '',
        text_en: '',
        photo: null,
      })
    }

    const deleteHandler = async () => {
      if(deleteItem.value || deleteItem.value === 0) {
        if (!form.fields.advantages[deleteItem.value].id.toString().includes('newItem')) {
          try {
            await deleteAdvantageAction({
              id: isPermanent.value ? 'permanent' : form.fields.id,
              advantageId: form.fields.advantages[deleteItem.value].id
            })
          } catch (e) {
            console.error(e)
          }
        }
        if (contentDeleteAdvantageData.value || form.fields.advantages[deleteItem.value].id.toString().includes('newItem')) {
          form.fields.advantages.splice(deleteItem.value, 1);
          contentData.value.data.advantages.splice(deleteItem.value, 1);
          deleteItem.value = null
        }
        changeVisibleModal(null)
      } else {
        try {
          await deleteContentAction({
            id: form.fields.id,
          })
          if (contentDeleteData.value) {
            await router.push({name: 'website', query: {tab: 'infrastructure'}})
          }
        } catch (e) {
          console.error(e)
        }
      }
    }

    const loadPriceMemory = (data) => {
      form.fields.price = data.files[0]
    }

    const deletePrice = () => {
      form.fields.price = null
    }

    watch(() => form.fields.link,
        () => {
          if (form.fields.link) {
            request_id.value = false
            form.fields.assigned_by = ''
          }
        })

    watch(() => request_id.value,
        () => {
          if (request_id.value) {
            form.fields.link = ''
          } else {
            form.fields.assigned_by = ''
          }
        })

    onBeforeMount(async () => {
      window.scrollTo(0, 0);
      if (isPermanent.value || route.params.id) {
        try {
          await getContentAction({id: isPermanent.value ? 'permanent' : route.params.id})
          if (contentData.value) {
            form.fields.id = contentData.value.data.id
            form.fields.advantages = [...contentData.value.data.advantages]
            form.fields.title_ru = contentData.value.data.title_ru
            form.fields.description_ru = contentData.value.data.description_ru
            form.fields.title_en = contentData.value.data.title_en ?? ''
            form.fields.description_en = contentData.value.data.description_en ?? ''
            form.fields.link = contentData.value.data.link
            form.fields.price = contentData.value.data.price
            request_id.value = contentData.value.data.request_id || false
            form.fields.assigned_by = contentData.value.data.assigned_by || ''
            form.fields.photos = JSON.parse(JSON.stringify(contentData.value.data.photos))
          }
        } catch (e) {
          console.error(e)
        }
      }
      breadcrumbs.value = [
        { label: 'Сайт / Инфраструктура', router: {name: 'website', query: {tab: 'infrastructure'}} },
        {label: contentData.value ? contentData.value.data.title_ru : 'Создание инфраструктуры'}
      ];
      isLoading.value = false
    });

    return {
      t,
      form,
      breadcrumbs,
      update,
      loadFileMemory,
      v$,
      errors,
      isLoading,
      deleteItem,
      changeAdvantages,
      deleteHandler,
      changeVisibleModal,
      addAdvantages,
      visible,
      validError,
      route,
      isPermanent,
      isContentDeleteAdvantageLoading,
      isUpdateLoading,
      isContentDeleteLoading,
      deleteSlide,
      updateCaption,
      loadPriceMemory,
      deletePrice,
      request_id,
      titleField,
      descriptionField,
    };
  }
});
</script>

<template>
  <ConfirmationModal :visible="visible">
    <template #header>
      Удалить {{ deleteItem !== null ? t(`confirmationModal.infrastructureItem.advantages`) : t(`confirmationModal.infrastructureItem.title`) }}
    </template>

    <template #default>
      <span>Вы уверены,  что хотите удалить {{ deleteItem !== null ? t(`confirmationModal.infrastructureItem.advantages`) : t(`confirmationModal.infrastructureItem.title`) }}? Это действие будет невозможно отменить</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between">
        <Button :label="t('labels.cancel')" @click="changeVisibleModal(null)" class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isContentDeleteAdvantageLoading || isContentDeleteLoading" @click="deleteHandler" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="py-2 mb-3">
    <div class="flex justify-content-between">
      <Breadcrumb v-if="!isPermanent" :data="breadcrumbs" separator="/"/>
      <div class="flex">
        <Button
            @click="update"
            :loading="isUpdateLoading"
            label="Сохранить изменения"
            :class="['btn-primary btn-fix-height font-light ml-3', {permanent: isPermanent}]"
        />
      </div>
    </div>
  </section>
  <section class="py-2 mb-3">
    <Skeleton v-if="isLoading" height="154px"></Skeleton>
    <MainCard v-else title="Заголовок" class="w-full">
      <span class="p-float-label mb-3 w-full">
        <InputText
          :class="['w-full', {'p-invalid': v$[titleField].$errors?.length || errors?.[titleField] }]"
          id="name_ru"
          v-model="form.fields[titleField]"
        />
        <label for="name_ru">Введите заголовок <i style="color: #E26B6B">*</i></label>
      </span>
      <span v-if="v$[titleField].$errors?.length || errors?.[titleField]" class="color-error">{{ errors?.[titleField] ? errors[titleField][0] : t(v$[titleField].$errors[0].$message) }}</span>
    </MainCard>
  </section>
  <section class="py-2 mb-3">
    <span class="infrastructure__slides-title">Изображения слайдера</span>
    <div class="infrastructure__slides">
        <InfrastructureImages
            v-for="(file, i) in form.fields.photos"
            :item="file"
            :index="i"
            :key="file.id"
            @deleteSlide="deleteSlide"
            @updateCaption="updateCaption"
        />
        <div class="infrastructure-grid__button">
          <ButtonFileUpload
              withPlus
              @chooseFiles="loadFileMemory"
              label="Добавить слайд"
              :multiple="false"
              :clear-files-after-select="true"
          />
        </div>
      </div>
    <span v-if="v$.photos.$errors.length || errors?.photo" class="flex text-xs color-error mb-2 mt-2">
              {{ errors?.photo ? errors.photo[0] : t(v$.photos.$errors[0].$message) }}
    </span>
  </section>
  <section class="py-2 mb-3">
    <MainCard title="Описание">
      <div class="grid">
        <div class="col-12">
          <Editor v-model="form.fields[descriptionField]" class="w-full"></Editor>
          <span v-if="v$[descriptionField].$errors?.length || errors?.[descriptionField]" class="color-error">{{ errors?.[descriptionField] ? errors[descriptionField][0] : t(v$[descriptionField].$errors[0].$message) }}</span>
        </div>
      </div>
    </MainCard>
  </section>
  <section class="py-2 mb-3">
    <Skeleton v-if="isLoading" height="154px"></Skeleton>
    <MainCard v-else title="Ссылка" class="w-full">
            <span class="p-float-label mb-3 w-full">
              <InputText
                  :class="['w-full', {'p-invalid': errors?.link }]"
                  id="name_ru"
                  v-model="form.fields.link"
              />
              <label for="name_ru">Вставьте ссылку</label>
            </span>
      <span v-if="errors?.link" class="color-error">{{ errors.link[0] }}</span>
    </MainCard>
  </section>
  <section class="py-2 mb-3">
    <Skeleton v-if="isLoading" height="154px"></Skeleton>
    <MainCard v-else title="ID ответственного в Б24" class="w-full">
      <div class="infrastructure__wrapper">
        <div class="infrastructure__wrapper-field">
          <Checkbox
              v-model="request_id"
              binary
              inputId="request_id"
              name="request_id"
          />
          <label class="ml-2 cursor-pointer" for="request_id">Отображение формы</label>
        </div>
        <div>
          <span class="p-float-label mb-3 w-full">
              <InputText
                  :class="['w-full', {'p-invalid': errors?.assigned_by || v$.assigned_by?.$errors?.length }]"
                  id="name_ru"
                  :disabled="!request_id"
                  v-model="form.fields.assigned_by"
              />
              <label for="name_ru">Введите ID</label>
            </span>
          <span v-if="v$.assigned_by?.$errors?.length" class="color-error">{{ t('Required field') }}</span>
        </div>
      </div>
    </MainCard>
  </section>
  <section class="py-2 mb-3">
    <Skeleton v-if="isLoading" width="100%" height="155px"></Skeleton>
    <MainCard v-else title="Прайс-лист" >
      <div class="grid">
        <template v-if="form.fields.price">
          <div>
            <div v-if="form.fields.price" class="card__wrapper">
              <img src="@/assets/images/icons/file-text.svg">
              <span>{{ form.fields.price.name ? form.fields.price.name : form.fields.price.split('/').pop() }}</span>
              <button @click="deletePrice" class="card__delete">x</button>
            </div>
          </div>
        </template>
        <div class="col-12">
          <ButtonFileUpload
              accept="application/pdf"
              @chooseFiles="loadPriceMemory"
              :label="form.fields.price ? 'Изменить файл' : 'Добавить файл'"
              :multiple="false"
              :clear-files-after-select="true"
          />
        </div>
      </div>
    </MainCard>
  </section>
  <section class="py-2 mb-3">
    <h1 class="mb-4">Преимущества</h1>
    <div v-if="form.fields.advantages.length" class="infrastructure__slides">
      <BrandCard
          v-for="(item, i) in form.fields.advantages"
          :key="item.id"
          scope="advantages"
          :slide="item"
          :index="i"
          :title="t(`websiteCard.advantages.title`)"
          :inputTitle="t(`websiteCard.advantages.input`)"
          :imgTitle="t(`websiteCard.advantages.image`)"
          :rows="4"
          isIcon
          @deleteSlide="changeVisibleModal"
          @changeSlide="changeAdvantages"
          :validError="validError"
          :errors="errors && errors.item === item ? errors.error : null"
      />
    </div>
    <Skeleton v-if="isLoading" height="121px"></Skeleton>
    <div class="infrastructure__slides mt-4">
      <WebsiteAddButton
          @clickHandler="addAdvantages"
          :title="`Добавить ${ t(`confirmationModal.infrastructureItem.advantages`) }`"
      />
    </div>
  </section>
  <section v-if="route.params.id && !isPermanent" class="py-2 mb-4">
    <div class="flex justify-content-end">
      <span class="color-error underline cursor-pointer" @click="changeVisibleModal(null)">
        Удалить
      </span>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.infrastructure {
  &__slides {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    column-gap: 30px;
    row-gap: 16px;
    word-break: break-all;
    @media screen and (min-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media screen and (min-width: 1400px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}
.permanent {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
}
.infrastructure-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 30px;
  row-gap: 16px;
}
.infrastructure-grid__button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #C3987A;
  min-height: 412px;
  border-radius: 10px;
  position: relative;
  :deep(.btn-primary__svg) {
    position: absolute;
    top: calc(50% + 16px);
    pointer-events: none;
  }
  :deep(.p-button-label) {
    background: unset;
    color: #C3987A;
  }
  :deep(.btn-primary) {
    width: 100%;
    height: 100%;
    background: unset !important;
    border-color: transparent !important;
  }
}
.card__wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #DBD9D9;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  span {
    word-break: break-all;
  }
}
.card__delete {
  border: none;
  background: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 20px;
}
.infrastructure__slides-title {
  display: flex;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 15px;
}
.infrastructure__wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

</style>